import { useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Download, Upload, RotateCcw, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import {
  parsePromptFile,
  describeChanges,
  downloadBundle,
} from "@/lib/promptBundle";

const ACTION_STYLE = {
  changed: "bg-sky-50 text-sky-800 border-sky-200",
  added: "bg-emerald-50 text-emerald-700 border-emerald-200",
  removed: "bg-red-50 text-red-700 border-red-200",
};

const BTN =
  "inline-flex items-center gap-1.5 px-3 py-1.5 text-sm border border-[var(--border)] hover:bg-[var(--secondary)] disabled:opacity-40 disabled:cursor-not-allowed";

/**
 * Settings → Prompts backup. Export every prompt for the active property
 * as one JSON file, import a file (partial files allowed), or reset all
 * prompts to the shipped defaults. Import and reset always show a
 * preview of what will change (server dry run) before anything is saved.
 *
 * `dirty` — the Settings form has unsaved edits. Import/reset reload the
 * form from the server, which would silently discard those edits, so
 * both are disabled until the editor saves or discards.
 */
export default function PromptBackupSection({ dirty, propertyName }) {
  const qc = useQueryClient();
  const fileRef = useRef(null);
  const [busy, setBusy] = useState("");
  // { kind: "import" | "reset", bundle?, filename?, sourceProperty?,
  //   changes: [], ignored: [] }
  const [plan, setPlan] = useState(null);
  const [backupFirst, setBackupFirst] = useState(true);

  const errMsg = (e, fallback) =>
    e?.response?.data?.detail || e?.message || fallback;

  const exportNow = async () => {
    const { filename, bundle } = await api.exportPrompts();
    downloadBundle(filename, bundle);
    return filename;
  };

  const onExport = async () => {
    setBusy("export");
    try {
      const name = await exportNow();
      toast.success(`Downloaded ${name}`);
    } catch (e) {
      toast.error(errMsg(e, "Export failed"));
    } finally {
      setBusy("");
    }
  };

  const onPickFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setBusy("import");
    try {
      const text = await file.text();
      const { bundle, error } = parsePromptFile(text, file.size);
      if (error) {
        toast.error(error);
        return;
      }
      const res = await api.importPrompts(bundle, true);
      setPlan({
        kind: "import",
        bundle,
        filename: file.name,
        sourceProperty: res.source_property || "",
        changes: res.changes || [],
        ignored: res.ignored || [],
      });
    } catch (err) {
      toast.error(errMsg(err, "Could not read that file"));
    } finally {
      setBusy("");
    }
  };

  const onResetPreview = async () => {
    setBusy("reset");
    try {
      const res = await api.resetPrompts(true);
      setPlan({ kind: "reset", changes: res.changes || [], ignored: [] });
    } catch (e) {
      toast.error(errMsg(e, "Could not load the defaults"));
    } finally {
      setBusy("");
    }
  };

  const onApply = async () => {
    if (!plan) return;
    setBusy("apply");
    try {
      if (backupFirst) await exportNow();
      const res =
        plan.kind === "import"
          ? await api.importPrompts(plan.bundle, false)
          : await api.resetPrompts(false);
      const summary = describeChanges(res.changes);
      toast.success(
        plan.kind === "import"
          ? `Imported — ${summary}`
          : `Reset to defaults — ${summary}`
      );
      setPlan(null);
      qc.invalidateQueries({ queryKey: ["settings"] });
      qc.invalidateQueries({ queryKey: ["writers"] });
    } catch (e) {
      toast.error(errMsg(e, "Nothing was saved"));
    } finally {
      setBusy("");
    }
  };

  const otherProperty =
    plan?.kind === "import" &&
    plan.sourceProperty &&
    propertyName &&
    plan.sourceProperty !== propertyName;

  return (
    <div className="space-y-4">
      <p className="text-xs text-[var(--ink-muted)] leading-relaxed">
        Every AI prompt on this property &mdash; editorial brief, research
        prompt, house rules, writers, social, fact check, print edition,
        source note, image style, comment rules and newsletter prompts
        &mdash; in one JSON file. Import accepts a full or partial file:
        only the prompts in the file are changed. WordPress connections,
        sources and other settings are never touched.
      </p>

      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          data-testid="prompts-export"
          onClick={onExport}
          disabled={!!busy}
          className={BTN}
        >
          {busy === "export" ? (
            <Loader2 className="w-3.5 h-3.5 animate-spin" />
          ) : (
            <Download className="w-3.5 h-3.5" />
          )}
          Export prompts
        </button>
        <button
          type="button"
          data-testid="prompts-import"
          onClick={() => fileRef.current?.click()}
          disabled={!!busy || dirty || !!plan}
          className={BTN}
        >
          {busy === "import" ? (
            <Loader2 className="w-3.5 h-3.5 animate-spin" />
          ) : (
            <Upload className="w-3.5 h-3.5" />
          )}
          Import prompts&hellip;
        </button>
        <button
          type="button"
          data-testid="prompts-reset"
          onClick={onResetPreview}
          disabled={!!busy || dirty || !!plan}
          className={`${BTN} text-red-600`}
        >
          {busy === "reset" ? (
            <Loader2 className="w-3.5 h-3.5 animate-spin" />
          ) : (
            <RotateCcw className="w-3.5 h-3.5" />
          )}
          Reset to defaults&hellip;
        </button>
        <input
          ref={fileRef}
          type="file"
          accept=".json,application/json"
          className="hidden"
          onChange={onPickFile}
          data-testid="prompts-import-file"
        />
      </div>

      {dirty && (
        <p className="text-[11px] text-[var(--ink-muted)]">
          You have unsaved changes on this page. Save or discard them before
          importing or resetting prompts. Export downloads the last saved
          version.
        </p>
      )}

      {plan && (
        <div
          data-testid="prompts-plan"
          className="border border-[var(--border)] bg-white p-4 space-y-3"
        >
          <div>
            <div className="font-serif-display text-base">
              {plan.kind === "import"
                ? `Import from ${plan.filename}`
                : "Reset every prompt to the shipped defaults"}
            </div>
            <div className="text-xs text-[var(--ink-muted)] mt-0.5">
              {plan.changes.length
                ? `${describeChanges(plan.changes)} — nothing is saved until you click Apply.`
                : "Nothing to change — this property already matches."}
            </div>
          </div>

          {otherProperty && (
            <div className="text-xs px-3 py-2 border bg-amber-50 text-amber-800 border-amber-200">
              This file was exported from &ldquo;{plan.sourceProperty}&rdquo;,
              not &ldquo;{propertyName}&rdquo;. Its prompts will replace this
              property&rsquo;s.
            </div>
          )}

          {plan.kind === "reset" && plan.changes.length > 0 && (
            <div className="text-xs px-3 py-2 border bg-amber-50 text-amber-800 border-amber-200">
              Every customised prompt below goes back to the built-in
              default, and writers that aren&rsquo;t built in are removed.
              Articles already written are not affected.
            </div>
          )}

          {plan.changes.length > 0 && (
            <ul className="max-h-72 overflow-auto divide-y divide-[var(--border)] border border-[var(--border)]">
              {plan.changes.map((c) => (
                <li
                  key={c.path}
                  className="flex items-center justify-between gap-3 px-3 py-1.5 text-xs"
                >
                  <span className="truncate">{c.label}</span>
                  <span
                    className={`shrink-0 px-1.5 py-0.5 border text-[10px] uppercase tracking-wider font-mono-display ${
                      ACTION_STYLE[c.action] || ""
                    }`}
                  >
                    {c.action}
                  </span>
                </li>
              ))}
            </ul>
          )}

          {plan.ignored.length > 0 && (
            <p className="text-[11px] text-[var(--ink-muted)]">
              Ignored (not a prompt this Newsdesk knows):{" "}
              {plan.ignored.join(", ")}
            </p>
          )}

          <div className="flex flex-wrap items-center justify-between gap-3 pt-1">
            {plan.changes.length > 0 ? (
              <label className="inline-flex items-center gap-2 text-xs">
                <input
                  type="checkbox"
                  checked={backupFirst}
                  onChange={(e) => setBackupFirst(e.target.checked)}
                  className="w-4 h-4 accent-[var(--accent)]"
                  data-testid="prompts-backup-first"
                />
                Download a backup of the current prompts first
              </label>
            ) : (
              <span />
            )}
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setPlan(null)}
                disabled={busy === "apply"}
                className={BTN}
              >
                {plan.changes.length ? "Cancel" : "Close"}
              </button>
              {plan.changes.length > 0 && (
                <button
                  type="button"
                  data-testid="prompts-apply"
                  onClick={onApply}
                  disabled={busy === "apply"}
                  className="inline-flex items-center gap-1.5 px-4 py-1.5 text-sm bg-[var(--accent)] text-white hover:bg-[var(--accent-hover)] disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {busy === "apply" && (
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  )}
                  {plan.kind === "import" ? "Apply import" : "Reset prompts"}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
